import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";

// ✅ Do NOT import/register ChartDataLabels here
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
);

export default function IntensityPlotAudio({ intensity, currentTime = 0 }) {
  const [isMobile, setIsMobile] = useState(false);

  const xs = intensity?.intensity?.xs || [];
  const ys = intensity?.intensity?.ys || [];
  const hasData = xs.length > 0 && ys.length > 0;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { yMin, yMax, yRange } = useMemo(() => {
    if (!hasData) return { yMin: 0, yMax: 1, yRange: 1 };
    const min = Math.min(...ys);
    const max = Math.max(...ys);
    const range = Math.max(1e-9, max - min);
    return { yMin: min, yMax: max, yRange: range };
  }, [hasData, ys]);

  const data = useMemo(() => {
    if (!hasData) return { datasets: [] };

    return {
      datasets: [
        {
          label: "Intensity (dB)",
          data: xs.map((x, i) => ({ x, y: ys[i] })),
          borderColor: "#2563eb",
          borderWidth: isMobile ? 1.5 : 2,
          tension: isMobile ? 0.2 : 0.35,
          pointRadius: 0,
          spanGaps: true,
          // ✅ hard-disable datalabels per dataset
          datalabels: { display: false },
        },
        {
          label: "Playback cursor",
          data: [
            { x: currentTime, y: yMin - yRange * 0.1 },
            { x: currentTime, y: yMax + yRange * 0.1 },
          ],
          borderColor: "rgba(16,185,129,1)",
          borderWidth: isMobile ? 1.5 : 2,
          pointRadius: 0,
          showLine: true,
          datalabels: { display: false },
        },
      ],
    };
  }, [hasData, xs, ys, currentTime, isMobile, yMin, yMax, yRange]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      elements: {
        point: { radius: 0 },
      },
      scales: {
        x: {
          type: "linear",
          grid: { display: true, color: "rgba(0,0,0,0.05)" },
          ticks: { display: false },
          title: {
            display: !isMobile,
            text: "Time (seconds)",
            font: { size: isMobile ? 12 : 14 },
          },
        },
        y: {
          min: yMin - yRange * 0.15,
          max: yMax + yRange * 0.1,
          grid: { color: "rgba(0,0,0,0.05)" },
          title: {
            display: true,
            text: "Intensity (dB)",
            font: { size: isMobile ? 12 : 14 },
          },
          ticks: {
            font: { size: isMobile ? 10 : 12 },
            callback: (v) => Number(v).toFixed(1), // ✅ one decimal
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
        },
        // ✅ hard-disable ChartDataLabels even if registered globally
        datalabels: { display: false },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
    }),
    [isMobile, yMin, yMax, yRange]
  );

  if (!hasData) return null;

  return (
    <div className="w-full min-w-0">
      <div
        className={`w-full bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
      >
        <Line data={data} options={options} />
      </div>
    </div>
  );
}